import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PersonalizedRecommendations = ({ recommendations, userPreferences }) => {
  const navigate = useNavigate();

  const handleViewCharity = (charityId) => {
    navigate(`/charity-profile?id=${charityId}`);
  };

  const handleDismissRecommendation = (recommendationId) => {
    console.log('Dismissing recommendation:', recommendationId);
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case 'similar_donors': return 'Users';
      case 'past_donations': return 'History';
      case 'trending': return 'TrendingUp';
      case 'location': return 'MapPin';
      case 'category_match': return 'Heart';
      default: return 'Sparkles';
    }
  };

  const getReasonText = (reason) => {
    switch (reason) {
      case 'similar_donors': return 'Popular with similar donors';
      case 'past_donations': return 'Based on your giving history';
      case 'trending': return 'Trending in your area';
      case 'location': return 'Local to your area';
      case 'category_match': return 'Matches your interests';
      default: return 'Recommended for you';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Recommended for You</h3>
          <p className="text-sm text-muted-foreground">Charities you might want to support</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/cause-discovery')}
          iconName="ExternalLink"
        >
          View All
        </Button>
      </div>
      {/* User Preferences Summary */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="User" size={16} className="text-primary" />
          <span className="text-sm font-medium text-card-foreground">Your Giving Profile</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {userPreferences?.categories?.map((category) => (
            <span
              key={category}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
            >
              {category}
            </span>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Average donation: ${userPreferences?.averageDonation} • Preferred frequency: {userPreferences?.frequency}
        </p>
      </div>
      {/* Recommendations */}
      <div className="space-y-4">
        {recommendations?.map((recommendation) => (
          <div key={recommendation?.id} className="border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-smooth">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3 flex-1">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Heart" size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-card-foreground">{recommendation?.name}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {recommendation?.description}
                  </p>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                    <span>{recommendation?.category}</span>
                    <span>•</span>
                    <span>{recommendation?.location}</span>
                    <span>•</span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Star" size={12} className="text-warning" />
                      <span>{recommendation?.rating}</span>
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDismissRecommendation(recommendation?.id)}
                iconName="X"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name={getReasonIcon(recommendation?.reason)} size={14} className="text-primary" />
                <span className="text-xs text-muted-foreground">
                  {getReasonText(recommendation?.reason)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-success">
                  {recommendation?.matchScore}% match
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewCharity(recommendation?.id)}
                >
                  View Details
                </Button>
              </div>
            </div>

            {/* Impact Preview */}
            {recommendation?.impactPreview && (
              <div className="mt-3 p-3 bg-success/5 border border-success/20 rounded-lg">
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name="Target" size={14} className="text-success" />
                  <span className="text-xs font-medium text-success">Potential Impact</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {recommendation?.impactPreview}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Customize Recommendations */}
      <div className="mt-6 pt-4 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/settings')}
          iconName="Settings"
          iconPosition="left"
          className="w-full justify-center"
        >
          Customize Recommendations
        </Button>
      </div>
    </div>
  );
};

export default PersonalizedRecommendations;